<template>
  <div class="homepage-light">
    <nav-bar />

    <section class="legal-section">
      <div class="container">
        <span class="section-label">{{ isEn ? 'Legal' : 'Légal' }}</span>
        <h1>{{ isEn ? 'Privacy Policy' : 'Politique de confidentialité' }}</h1>
        <p class="legal-date">
          {{ isEn ? 'Last updated:' : 'Dernière mise à jour :' }} {{ lastUpdatedDate }}
        </p>

        <div class="legal-content">
          <template v-if="!isEn">
            <h2>1. Responsable du traitement</h2>
            <p>
              <strong>Minds' Academy</strong>, établie en Tunisie, est responsable du traitement
              des données personnelles collectées via le site
              <a href="https://mindsacademy.io">mindsacademy.io</a>.
            </p>

            <h2>2. Données collectées</h2>
            <p>Nous collectons uniquement les données que vous nous transmettez volontairement :</p>
            <ul>
              <li><strong>Formulaire de contact :</strong> nom, prénom, adresse e-mail, message.</li>
              <li><strong>Compte utilisateur :</strong> nom complet, adresse e-mail, mot de passe (stocké de façon chiffrée), rôle (étudiant, enseignant ou administration) et, le cas échéant, niveau scolaire (1A à 5A).</li>
              <li><strong>Espace pédagogique :</strong> groupes, supports de cours, liens, fichiers PDF et examens que vous consultez ou que vos enseignants publient pour votre niveau.</li>
            </ul>
            <p>Nous ne collectons aucune donnée sensible (santé, opinion politique, religion, etc.).</p>

            <h2>3. Finalités du traitement</h2>
            <p>Les données sont utilisées exclusivement pour :</p>
            <ul>
              <li>Répondre à vos demandes via le formulaire de contact.</li>
              <li>Créer et gérer votre compte Minds' Academy.</li>
              <li>Vous donner accès aux cours en vidéo, Free Sessions et ressources de votre niveau.</li>
              <li>Permettre aux enseignants et à l'administration de publier des supports pour leurs groupes.</li>
              <li>Assurer le bon fonctionnement, la sécurité et l'amélioration de la plateforme.</li>
            </ul>

            <h2>4. Durée de conservation</h2>
            <p>
              Les données de contact sont conservées pendant une durée maximale de
              <strong>24 mois</strong> à compter de votre dernière interaction.
              Les données liées à un compte utilisateur sont conservées tant que le compte est actif,
              puis supprimées dans un délai de 6 mois après sa clôture.
            </p>

            <h2>5. Partage des données</h2>
            <p>
              Vos données ne sont jamais vendues ni cédées à des tiers.
              Elles peuvent être transmises à des sous-traitants techniques
              (hébergement, envoi d'e-mails) dans le strict cadre de la fourniture du service,
              sous contrat garantissant la confidentialité.
            </p>

            <h2>6. Sécurité</h2>
            <p>
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées
              pour protéger vos données contre tout accès non autorisé, perte ou altération
              (chiffrement SSL/TLS, mots de passe hachés, accès restreint).
            </p>

            <h2>7. Vos droits</h2>
            <p>Conformément à la réglementation tunisienne applicable à la protection des données personnelles, vous disposez notamment des droits suivants :</p>
            <ul>
              <li>Droit d'accès à vos données personnelles.</li>
              <li>Droit de rectification des données inexactes.</li>
              <li>Droit à l'effacement (« droit à l'oubli »).</li>
              <li>Droit d'opposition au traitement.</li>
              <li>Droit à la portabilité de vos données.</li>
            </ul>
            <p>
              Pour exercer ces droits, contactez-nous à
              <a href="mailto:contact@mindsacademy.io">contact@mindsacademy.io</a>
              ou via notre <router-link to="/contact">
                formulaire de contact
              </router-link>.
            </p>

            <h2>8. Cookies</h2>
            <p>
              Le site Minds' Academy utilise uniquement des cookies strictement nécessaires
              au fonctionnement du service (session, authentification).
              Aucun cookie publicitaire ou de traçage n'est utilisé.
            </p>

            <h2>9. Services tiers</h2>
            <p>
              Certaines vidéos de cours sont hébergées sur YouTube et lues via un lecteur intégré.
              Lorsque vous lancez une vidéo, YouTube (Google) peut collecter des données selon
              sa propre politique de confidentialité. Nous n'avons pas accès à ces données.
            </p>

            <h2>10. Modification de cette politique</h2>
            <p>
              Nous nous réservons le droit de modifier cette politique de confidentialité.
              Toute modification sera publiée sur cette page avec une date de mise à jour actualisée.
            </p>
          </template>

          <template v-else>
            <h2>1. Data controller</h2>
            <p>
              <strong>Minds' Academy</strong>, established in Tunisia, is responsible for processing
              personal data collected via
              <a href="https://mindsacademy.io">mindsacademy.io</a>.
            </p>

            <h2>2. Data we collect</h2>
            <p>We only collect data that you voluntarily provide to us:</p>
            <ul>
              <li><strong>Contact form:</strong> last name, first name, email address, message.</li>
              <li><strong>User account:</strong> full name, email address, password (stored hashed), role (student, teacher or administration) and, where applicable, academic level (1A to 5A).</li>
              <li><strong>Learning space:</strong> groups, course materials, links, PDF files and exams that you access or that teachers publish for your level.</li>
            </ul>
            <p>We do not collect any sensitive data (health, political opinions, religion, etc.).</p>

            <h2>3. Purposes of processing</h2>
            <p>Your data is used exclusively to:</p>
            <ul>
              <li>Respond to your requests via the contact form.</li>
              <li>Create and manage your Minds' Academy account.</li>
              <li>Give you access to video courses, Free Sessions and resources for your level.</li>
              <li>Allow teachers and administrators to publish materials for their groups.</li>
              <li>Operate, secure and improve the platform.</li>
            </ul>

            <h2>4. Data retention</h2>
            <p>
              Contact data is retained for a maximum of <strong>24 months</strong> from your last interaction.
              Account-related data is retained as long as the account is active, then deleted within 6 months after the account is closed.
            </p>

            <h2>5. Data sharing</h2>
            <p>
              Your data is never sold or transferred to third parties.
              It may be shared with technical service providers (hosting, email delivery) strictly for providing the service,
              under agreements ensuring confidentiality.
            </p>

            <h2>6. Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your data against unauthorized access, loss or alteration
              (SSL/TLS encryption, hashed passwords, restricted access).
            </p>

            <h2>7. Your rights</h2>
            <p>In accordance with applicable Tunisian personal data protection rules, you have the following rights:</p>
            <ul>
              <li>Right of access to your personal data.</li>
              <li>Right to rectify inaccurate data.</li>
              <li>Right to erasure (“right to be forgotten”).</li>
              <li>Right to object to processing.</li>
              <li>Right to data portability.</li>
            </ul>
            <p>
              To exercise these rights, contact us at
              <a href="mailto:contact@mindsacademy.io">contact@mindsacademy.io</a>
              or via our <router-link to="/contact">
                contact form
              </router-link>.
            </p>

            <h2>8. Cookies</h2>
            <p>
              Minds' Academy uses only cookies that are strictly necessary for service operation (session, authentication).
              No advertising or tracking cookies are used.
            </p>

            <h2>9. Third-party services</h2>
            <p>
              Some course videos are hosted on YouTube and played through an embedded player.
              When you start a video, YouTube (Google) may collect data under its own privacy policy.
              We do not have access to that data.
            </p>

            <h2>10. Changes to this policy</h2>
            <p>
              We reserve the right to modify this privacy policy. Any changes will be published on this page with an updated date.
            </p>
          </template>
        </div>
      </div>
    </section>

    <page-footer />
  </div>
</template>

<script>
import navBar from '@/views/home/HomePage2/components/navBar.vue'
import pageFooter from '@/views/home/HomePage2/components/pageFooter.vue'

export default {
  name: 'PrivacyPolicy',
  components: { navBar, pageFooter },
  computed: {
    isEn() {
      return this.$i18n.locale === 'en'
    },
    lastUpdatedDate() {
      const d = new Date('2026-09-20T00:00:00Z')
      const loc = this.isEn ? 'en-US' : 'fr-FR'
      return d.toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric' })
    },
    documentTitle() {
      return this.isEn ? "Privacy Policy | Minds' Academy" : "Politique de confidentialité | Minds' Academy"
    },
  },
  watch: {
    '$i18n.locale': function () {
      document.title = this.documentTitle
    },
  },
  mounted() {
    document.title = this.documentTitle
    window.scrollTo(0, 0)
  },
  beforeDestroy() {
    document.title = "Minds' Academy"
  },
}
</script>

<style scoped>
.legal-section {
  padding: 120px 0 80px;
  background:
    radial-gradient(700px 340px at 20% 20%, rgba(0,163,255,0.06) 0%, rgba(255,255,255,0) 65%),
    linear-gradient(180deg, #ffffff 0%, var(--bg-sky) 50%, #ffffff 100%);
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 24px;
}

.section-label {
  display: inline-block;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--brand-blue-deep);
  background: rgba(0, 163, 255, 0.10);
  border: 1px solid rgba(0, 163, 255, 0.18);
  padding: 6px 16px;
  border-radius: 50px;
  margin-bottom: 16px;
}

h1 {
  font-size: clamp(28px, 4vw, 38px);
  font-weight: 800;
  color: var(--ink);
  line-height: 1.15;
  margin: 0 0 8px;
  letter-spacing: -0.03em;
}

.legal-date {
  font-size: 14px;
  color: var(--muted);
  margin-bottom: 40px;
}

.legal-content {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.07);
}

.legal-content h2 {
  font-size: 20px;
  font-weight: 700;
  color: var(--ink);
  margin: 32px 0 12px;
}

.legal-content h2:first-child {
  margin-top: 0;
}

.legal-content p {
  font-size: 15px;
  color: var(--muted);
  line-height: 1.75;
  margin: 0 0 12px;
}

.legal-content ul {
  margin: 0 0 16px;
  padding-left: 24px;
}

.legal-content li {
  font-size: 15px;
  color: var(--muted);
  line-height: 1.75;
  margin-bottom: 6px;
}

.legal-content a {
  color: var(--brand-blue);
  text-decoration: none;
}

.legal-content a:hover {
  text-decoration: underline;
}

@media (max-width: 600px) {
  .legal-content { padding: 24px; }
  h1 { font-size: 26px; }
}
</style>
